/**
 * PermissionsSection — نظام الصلاحيات ذو المستويين:
 *   1) صلاحيات المسميات الوظيفية (Roles) — تسري على كل حاملي المسمى.
 *   2) تخصيص لموظف معيّن — منح/حجب فوق صلاحيات مسماه (تُحفظ الفروقات فقط).
 *
 * Views: landing → roles → role-edit | employees → employee-edit
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Shield, Users, ArrowRight, Save, Loader2, User, BadgeCheck, UserCog, ChevronLeft,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import PermissionGrid from './PermissionGrid';
import { PAGE_INDEX } from '../../../permissions/registry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---- helpers: diff draft vs role base → overrides deltas ----
const KEYS = ['view', 'edit', 'delete'];
const diffPerms = (draft, base) => {
  const out = {};
  const pages = new Set([...Object.keys(draft || {}), ...Object.keys(base || {})]);
  pages.forEach((p) => {
    KEYS.forEach((k) => {
      const d = !!draft?.[p]?.[k];
      const b = !!base?.[p]?.[k];
      if (d !== b) (out[p] = out[p] || {})[k] = d;
    });
  });
  return out;
};
const diffAccess = (draft, base) => {
  const out = {};
  new Set([...Object.keys(draft || {}), ...Object.keys(base || {})]).forEach((k) => {
    if (!!draft?.[k] !== !!base?.[k]) out[k] = !!draft?.[k];
  });
  return out;
};
const countGranted = (perms) =>
  Object.values(perms || {}).filter((v) => v && (v.view || v.edit || v.delete)).length;

export default function PermissionsSection({
  language,
  loadingAdmins,
  allAdmins,
  headers,
}) {
  const isAr = language === 'ar';

  const [view, setView] = useState('landing'); // landing | roles | role-edit | employees | employee-edit
  const [jobTitles, setJobTitles] = useState([]);
  const [rolePermsList, setRolePermsList] = useState([]); // configured role docs
  const [loadingRoles, setLoadingRoles] = useState(false);

  // role editor
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleDraft, setRoleDraft] = useState({ permissions: {}, platform_access: {} });
  const [savingRole, setSavingRole] = useState(false);

  // employee editor
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [empLoading, setEmpLoading] = useState(false);
  const [empDraft, setEmpDraft] = useState({ permissions: {}, platform_access: {} });
  const [roleBase, setRoleBase] = useState({ permissions: {}, platform_access: {} });
  const [empJobTitleId, setEmpJobTitleId] = useState('');
  const [savingEmp, setSavingEmp] = useState(false);

  const loadRolesData = useCallback(async () => {
    setLoadingRoles(true);
    try {
      const [titlesRes, rolesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dropdowns/job-titles?active_only=true`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/role-permissions`, { headers }),
      ]);
      setJobTitles(titlesRes.data?.data || []);
      setRolePermsList(rolesRes.data?.data || []);
    } catch {
      toast.error(isAr ? 'فشل تحميل المسميات الوظيفية' : 'Failed to load job titles');
    } finally {
      setLoadingRoles(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadRolesData(); }, [loadRolesData]);

  // ---------- toggle handlers ----------
  // Granting "view" on a page also grants "view" on every page above it (and opens its section),
  // otherwise the sub-button would be unreachable in the dashboard.
  const grantAncestors = (draft, pageId) => {
    const perms = { ...draft.permissions };
    const access = { ...draft.platform_access };
    let node = PAGE_INDEX[pageId];
    if (node) access[node.sectionKey] = true;
    while (node && node.parentId) {
      const parent = PAGE_INDEX[node.parentId];
      if (!parent) break;
      perms[parent.id] = { edit: false, delete: false, ...perms[parent.id], view: true };
      node = parent;
    }
    return { ...draft, permissions: perms, platform_access: access };
  };
  const makeToggle = (setter) => (pageId, key) => {
    setter((prev) => {
      const cur = prev.permissions?.[pageId] || { view: false, edit: false, delete: false };
      let next = { ...cur, [key]: !cur[key] };
      if (key === 'view' && !next.view) next = { view: false, edit: false, delete: false };
      if (key !== 'view' && next[key]) next.view = true;
      const draft = { ...prev, permissions: { ...prev.permissions, [pageId]: next } };
      return next.view ? grantAncestors(draft, pageId) : draft;
    });
  };
  const makeAccessToggle = (setter) => (platformKey) => {
    setter((prev) => ({
      ...prev,
      platform_access: { ...prev.platform_access, [platformKey]: !prev.platform_access?.[platformKey] },
    }));
  };
  const makeBulkToggle = (setter) => (key, value, pageIds) => {
    setter((prev) => {
      const next = { ...prev.permissions };
      pageIds.forEach((id) => {
        const cur = { view: false, edit: false, delete: false, ...next[id], [key]: value };
        if (key === 'view' && !value) { cur.edit = false; cur.delete = false; }
        if (key !== 'view' && value) cur.view = true;
        next[id] = cur;
      });
      let draft = { ...prev, permissions: next };
      if (value) pageIds.forEach((id) => { draft = grantAncestors(draft, id); });
      return draft;
    });
  };

  // ---------- role flow ----------
  const openRole = async (title) => {
    setSelectedRole(title);
    setView('role-edit');
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/role-permissions/${title.id || title._id}`, { headers },
      );
      setRoleDraft({
        permissions: res.data?.permissions || {},
        platform_access: res.data?.platform_access || {},
      });
    } catch {
      setRoleDraft({ permissions: {}, platform_access: {} });
    }
  };

  const saveRole = async () => {
    if (!selectedRole) return;
    setSavingRole(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/role-permissions/${selectedRole.id || selectedRole._id}`,
        roleDraft, { headers },
      );
      toast.success(
        isAr
          ? `تم حفظ صلاحيات "${selectedRole.name_ar}" وتطبيقها على كل حاملي المسمى`
          : `"${selectedRole.name_en}" permissions saved & propagated`,
      );
      loadRolesData();
      setView('roles');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSavingRole(false);
    }
  };

  // ---------- employee flow ----------
  const openEmployee = async (admin) => {
    setSelectedEmp(admin);
    setView('employee-edit');
    setEmpLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/effective-permissions/${admin.id}`, { headers },
      );
      const d = res.data || {};
      setRoleBase({ permissions: d.role_permissions || {}, platform_access: d.role_platform_access || {} });
      setEmpDraft({ permissions: d.effective_permissions || {}, platform_access: d.effective_platform_access || {} });
      setEmpJobTitleId(d.job_title_id || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل الصلاحيات' : 'Failed to load permissions'));
      setRoleBase({ permissions: {}, platform_access: {} });
      setEmpDraft({ permissions: {}, platform_access: {} });
      setEmpJobTitleId(admin.job_title_id || '');
    } finally {
      setEmpLoading(false);
    }
  };

  const saveEmployee = async () => {
    if (!selectedEmp) return;
    setSavingEmp(true);
    try {
      const title = jobTitles.find((t) => (t.id || t._id) === empJobTitleId);
      await axios.put(
        `${BACKEND_URL}/api/admin/permissions/${selectedEmp.id}`,
        {
          ...(title ? { job_title_id: empJobTitleId, job_title_ar: title.name_ar, job_title: title.name_en } : {}),
          permission_overrides: diffPerms(empDraft.permissions, roleBase.permissions),
          platform_access_overrides: diffAccess(empDraft.platform_access, roleBase.platform_access),
        },
        { headers },
      );
      toast.success(isAr ? 'تم حفظ صلاحيات الموظف' : 'Employee permissions saved');
      setView('employees');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSavingEmp(false);
    }
  };

  // ---------- shared UI bits ----------
  const BackBtn = ({ to, label }) => (
    <button
      onClick={() => setView(to)}
      className="flex items-center gap-1.5 text-xs text-[#c9a227] hover:text-[#e5c449] font-bold mb-4"
      data-testid="perm-back-btn"
    >
      <ArrowRight className="h-4 w-4 rtl:rotate-0 ltr:rotate-180" />
      {label}
    </button>
  );

  // ================= LANDING =================
  if (view === 'landing') {
    return (
      <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6" data-testid="permissions-landing">
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <Shield className="h-5 w-5 text-[#c9a227]" />
          {isAr ? 'صلاحيات المشرفين والأدمن' : 'Admin & Supervisor Permissions'}
        </h2>
        <p className="text-[#808080] text-sm mb-8">
          {isAr
            ? 'حدّد الصلاحيات على مستوى المسمى الوظيفي (Role)، ثم خصّص لموظف معيّن عند الحاجة'
            : 'Define permissions per job title (role), then customise per employee when needed'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <button
            onClick={() => setView('roles')}
            className="group bg-[#0a0a0a] rounded-2xl border border-[#2a2a4e] hover:border-[#c9a227]/60 p-8 text-center transition-all"
            data-testid="perm-landing-roles-card"
          >
            <div className="w-16 h-16 rounded-2xl bg-[#c9a227]/10 border border-[#c9a227]/30 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <BadgeCheck className="h-8 w-8 text-[#c9a227]" />
            </div>
            <h3 className="text-base font-bold text-white mb-1">
              {isAr ? 'تعديل صلاحيات المسميات الوظيفية' : 'Edit Role Permissions'}
            </h3>
            <p className="text-xs text-[#71717A]">
              {isAr
                ? 'كل مسمى وظيفي = Role — الصلاحيات تسري تلقائياً على كل حامليه'
                : 'Each job title = a Role — permissions apply to everyone holding it'}
            </p>
          </button>
          <button
            onClick={() => setView('employees')}
            className="group bg-[#0a0a0a] rounded-2xl border border-[#2a2a4e] hover:border-sky-500/60 p-8 text-center transition-all"
            data-testid="perm-landing-employee-card"
          >
            <div className="w-16 h-16 rounded-2xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <UserCog className="h-8 w-8 text-sky-400" />
            </div>
            <h3 className="text-base font-bold text-white mb-1">
              {isAr ? 'منح صلاحيات لموظف معيّن' : 'Grant Permissions to a Specific Employee'}
            </h3>
            <p className="text-xs text-[#71717A]">
              {isAr
                ? 'منح صلاحيات إضافية أو حجب صلاحيات عن موظف — فوق صلاحيات مسماه الوظيفي'
                : 'Extra grants or revokes for one employee — on top of their role'}
            </p>
          </button>
        </div>
      </div>
    );
  }

  // ================= ROLES LIST =================
  if (view === 'roles') {
    return (
      <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6" data-testid="permissions-roles-list">
        <BackBtn to="landing" label={isAr ? 'رجوع' : 'Back'} />
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <BadgeCheck className="h-5 w-5 text-[#c9a227]" />
          {isAr ? 'صلاحيات المسميات الوظيفية (Roles)' : 'Role Permissions (Job Titles)'}
        </h2>
        <p className="text-[#808080] text-sm mb-6">
          {isAr
            ? 'المسميات تُدار من: ASB606 ADMIN ← القوائم المنسدلة ← المسميات الوظيفية'
            : 'Titles are managed at: ASB606 ADMIN → Dropdown Lists → Job Titles'}
        </p>
        {loadingRoles ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#c9a227] mx-auto" />
          </div>
        ) : jobTitles.length === 0 ? (
          <div className="text-center py-12 text-[#808080]">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{isAr ? 'لا توجد مسميات وظيفية — أضفها من القوائم المنسدلة أولاً' : 'No job titles yet — add them in Dropdown Lists first'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {jobTitles.map((title) => {
              const tid = title.id || title._id;
              const doc = rolePermsList.find((r) => r.job_title_id === tid);
              const grantedPages = countGranted(doc?.permissions);
              return (
                <button
                  key={tid}
                  onClick={() => openRole(title)}
                  className="w-full bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e] flex items-center justify-between hover:border-[#c9a227] transition-colors text-start"
                  data-testid={`perm-role-item-${tid}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-full bg-[#c9a227]/15 flex items-center justify-center">
                      <BadgeCheck className="h-5 w-5 text-[#c9a227]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">{isAr ? title.name_ar : title.name_en}</p>
                      <p className="text-xs text-[#808080]">{isAr ? title.name_en : title.name_ar}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {grantedPages > 0 ? (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 font-bold">
                        {isAr ? `${grantedPages} صفحة مفعّلة` : `${grantedPages} pages granted`}
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/40">
                        {isAr ? 'غير مهيأ' : 'Not configured'}
                      </span>
                    )}
                    <ChevronLeft className="h-4 w-4 text-white/30 rtl:rotate-0 ltr:rotate-180" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ================= ROLE EDIT =================
  if (view === 'role-edit' && selectedRole) {
    return (
      <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6" data-testid="permissions-role-edit">
        <BackBtn to="roles" label={isAr ? 'رجوع للمسميات' : 'Back to roles'} />
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <BadgeCheck className="h-5 w-5 text-[#c9a227]" />
              {isAr ? `صلاحيات: ${selectedRole.name_ar}` : `Permissions: ${selectedRole.name_en}`}
            </h2>
            <p className="text-xs text-[#808080] mt-1">
              {isAr
                ? 'عند الحفظ تُطبَّق تلقائياً على كل الموظفين الحاملين لهذا المسمى'
                : 'On save, propagates to every employee holding this title'}
            </p>
          </div>
          <Button
            onClick={saveRole}
            disabled={savingRole}
            className="bg-[#c9a227] hover:bg-[#e5c449] text-black font-bold rounded-xl"
            data-testid="perm-save-role"
          >
            {savingRole ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}
            {isAr ? 'حفظ صلاحيات المسمى' : 'Save Role Permissions'}
          </Button>
        </div>
        <PermissionGrid
          language={language}
          permissions={roleDraft.permissions}
          onTogglePermission={makeToggle(setRoleDraft)}
          platformAccess={roleDraft.platform_access}
          onToggleAccess={makeAccessToggle(setRoleDraft)}
          onBulkToggle={makeBulkToggle(setRoleDraft)}
        />
      </div>
    );
  }

  // ================= EMPLOYEES LIST =================
  if (view === 'employees') {
    return (
      <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6" data-testid="permissions-employees-list">
        <BackBtn to="landing" label={isAr ? 'رجوع' : 'Back'} />
        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
          <UserCog className="h-5 w-5 text-sky-400" />
          {isAr ? 'منح صلاحيات لموظف معيّن' : 'Grant Permissions to a Specific Employee'}
        </h2>
        {loadingAdmins ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#c9a227] mx-auto" />
          </div>
        ) : allAdmins.length === 0 ? (
          <div className="text-center py-12 text-[#808080]">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{isAr ? 'لا يوجد مشرفين' : 'No admins found'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {allAdmins.map((admin) => {
              const isSuper = admin.is_super_admin || (admin.username || '').toLowerCase() === 'admin';
              return (
              <button
                key={admin.id || admin.email}
                onClick={() => !isSuper && openEmployee(admin)}
                disabled={isSuper}
                className={`w-full bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e] flex items-center justify-between transition-colors text-start ${isSuper ? 'opacity-70 cursor-not-allowed' : 'hover:border-sky-500/60'}`}
                data-testid={`perm-emp-item-${admin.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-sky-500/15 flex items-center justify-center">
                    <User className="h-5 w-5 text-sky-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{admin.name || (isAr ? 'بدون اسم' : 'No name')}</p>
                    <p className="text-xs text-[#808080]">{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {isSuper ? (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-[#c9a227]/15 border border-[#c9a227]/40 text-[#c9a227] font-bold" data-testid="perm-super-admin-badge">
                      {isAr ? 'الأدمن الرئيسي — صلاحيات كاملة دائماً' : 'Super admin — always full access'}
                    </span>
                  ) : admin.job_title_ar ? (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-[#c9a227]/10 border border-[#c9a227]/30 text-[#c9a227] font-bold">
                      {admin.job_title_ar}
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300">
                      {isAr ? 'بدون مسمى وظيفي' : 'No job title'}
                    </span>
                  )}
                  <ChevronLeft className="h-4 w-4 text-white/30 rtl:rotate-0 ltr:rotate-180" />
                </div>
              </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ================= EMPLOYEE EDIT =================
  if (view === 'employee-edit' && selectedEmp) {
    return (
      <div className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6" data-testid="permissions-employee-edit">
        <BackBtn to="employees" label={isAr ? 'رجوع للموظفين' : 'Back to employees'} />
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <UserCog className="h-5 w-5 text-sky-400" />
              {selectedEmp.name || selectedEmp.email}
            </h2>
            <p className="text-xs text-[#808080] mt-1">{selectedEmp.email}</p>
          </div>
          <Button
            onClick={saveEmployee}
            disabled={savingEmp || empLoading}
            className="bg-sky-600 hover:bg-sky-500 text-white font-bold rounded-xl"
            data-testid="perm-save-emp"
          >
            {savingEmp ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}
            {isAr ? 'حفظ صلاحيات الموظف' : 'Save Employee Permissions'}
          </Button>
        </div>

        {/* المسمى الوظيفي (Role) للموظف — عرض فقط (التغيير من إدارة المشرفين) */}
        <div className="bg-[#0a0a0a] rounded-xl border border-[#2a2a4e] p-4 mb-4 flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs text-[#A1A1AA] font-bold mb-1.5">
              {isAr ? 'المسمى الوظيفي (Role) — أساس الصلاحيات' : 'Job Title (Role) — permission base'}
            </p>
            <span
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-bold ${empJobTitleId
                ? 'bg-[#c9a227]/10 border-[#c9a227]/30 text-[#c9a227]'
                : 'bg-amber-500/10 border-amber-500/30 text-amber-400'}`}
              data-testid="perm-emp-role-badge"
            >
              <BadgeCheck className="h-3.5 w-3.5" />
              {(() => {
                const t = jobTitles.find((j) => (j.id || j._id) === empJobTitleId);
                return t
                  ? (isAr ? t.name_ar : t.name_en)
                  : (isAr ? 'بدون مسمى وظيفي — صلاحيات شخصية فقط' : 'No job title — personal permissions only');
              })()}
            </span>
          </div>
          <p className="text-[10.5px] text-[#606060] leading-relaxed max-w-xs">
            {isAr
              ? 'لتغيير المسمى الوظيفي: إدارة المشرفين ← حسابات المشرفين والموظفين'
              : 'To change the job title: Admin Management → Supervisors & Staff Accounts'}
          </p>
        </div>

        {empLoading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-sky-400 mx-auto" />
            <p className="text-[#808080] mt-2 text-sm">{isAr ? 'جاري تحميل الصلاحيات...' : 'Loading permissions...'}</p>
          </div>
        ) : (
          <PermissionGrid
            language={language}
              permissions={empDraft.permissions}
            onTogglePermission={makeToggle(setEmpDraft)}
            platformAccess={empDraft.platform_access}
            onToggleAccess={makeAccessToggle(setEmpDraft)}
            onBulkToggle={makeBulkToggle(setEmpDraft)}
            roleBase={roleBase}
          />
        )}
      </div>
    );
  }

  return null;
}
